/**
 * Internal Matches modal — Talent Rediscovery.
 *
 * Opens for a single JobPosting. Loads any cached match rows on mount,
 * then offers a "Find Internal Candidates" button that runs the AI
 * scan (replaces all prior rows for the job).
 *
 * Each match renders as a card with score badge, matching skills,
 * gaps, AI rationale, dev-time estimate, and an "Add to candidates"
 * action that funnels them into the job's candidate pipeline as an
 * internal applicant. (For now we toast — the candidate-create flow
 * already handles internal source attribution.)
 */
import { useEffect, useState } from "react";
import { Loader2, Search, Sparkles } from "lucide-react";

import {
  listInternalMatches,
  rediscoverInternalCandidates,
  type InternalMatch,
  type JobPosting,
} from "@/api/recruiting";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/hooks/useToast";

interface InternalMatchesModalProps {
  job: JobPosting;
  onClose: () => void;
}

function scoreVariant(score: number): "success" | "warning" | "default" {
  if (score >= 80) return "success";
  if (score >= 50) return "warning";
  return "default";
}

export function InternalMatchesModal({ job, onClose }: InternalMatchesModalProps) {
  const { addToast } = useToast();
  const [matches, setMatches] = useState<InternalMatch[]>([]);
  const [lastScannedAt, setLastScannedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await listInternalMatches(job.id);
        if (cancelled) return;
        setMatches(r.matches);
        setLastScannedAt(r.last_scanned_at);
      } catch {
        if (!cancelled) addToast("error", "Could not load internal matches.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [job.id, addToast]);

  async function onScan() {
    if (
      !confirm(
        "Run an AI scan of every active employee against this job? Uses Claude credits (~$0.005 per scan).",
      )
    )
      return;
    setScanning(true);
    try {
      const r = await rediscoverInternalCandidates(job.id);
      setMatches(r.matches);
      setLastScannedAt(r.last_scanned_at);
      if (r.matches.length === 0) {
        addToast(
          "info",
          "No internal candidates matched ≥50% on this role. Try refining the job description.",
        );
      } else {
        addToast(
          "success",
          `Found ${r.matches.length} internal candidate${r.matches.length === 1 ? "" : "s"}.`,
        );
      }
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Scan failed.";
      addToast("error", detail);
    } finally {
      setScanning(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={`Internal candidates · ${job.title}`} size="xl">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-text-muted">
            {lastScannedAt
              ? `Last scanned ${new Date(lastScannedAt).toLocaleString()}.`
              : "No scan yet — click the button to find internal candidates."}{" "}
            Only employees with a match score of 50 or higher are shown.
          </p>
          <Button onClick={onScan} disabled={scanning || loading}>
            {scanning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Scanning…
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Find internal candidates
              </>
            )}
          </Button>
        </div>

        {loading ? (
          <div className="flex h-32 items-center justify-center text-text-muted">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : matches.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-10 text-center text-sm text-text-muted">
            <Search className="mx-auto mb-2 h-5 w-5" />
            No internal matches yet. Run a scan to surface candidates.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {matches.map((m) => (
              <div
                key={m.id}
                className="rounded-md border border-border bg-surface p-4"
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold">
                      {m.employee_name || "—"}
                    </h3>
                    <p className="text-xs text-text-muted">
                      {m.current_role || "—"}
                      {m.department && <> · {m.department}</>}
                    </p>
                  </div>
                  <Badge variant={scoreVariant(m.match_score)}>
                    {m.match_score}
                  </Badge>
                </div>

                {m.rationale && (
                  <p className="mb-2 text-sm">{m.rationale}</p>
                )}

                {m.matching_skills.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1">
                    {m.matching_skills.map((s, i) => (
                      <Badge key={i} variant="success" className="text-[11px]">
                        {s}
                      </Badge>
                    ))}
                  </div>
                )}
                {m.gaps.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1">
                    {m.gaps.map((s, i) => (
                      <Badge key={i} variant="warning" className="text-[11px]">
                        gap: {s}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 pt-2 text-xs text-text-muted">
                  <span>
                    {m.development_time
                      ? `Ready: ${m.development_time}`
                      : "Readiness: —"}
                  </span>
                  <a
                    href={`/employees/${m.employee_id}`}
                    className="text-accent hover:underline"
                  >
                    View profile →
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default InternalMatchesModal;
